import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Modal } from 'react-bootstrap';
import { IoDownloadOutline, IoChevronDownOutline } from 'react-icons/io5';
import GenerateDocumentButton from './generateDocumentButton';
import { BASE_LANGUAGE } from '../../../../utils/constants/marketLanguages';
import { ReactComponent as RemoveIcon } from '../../../../assets/remove.svg';

const formatDate = (value) => new Date(value).toLocaleString('sk-SK', { dateStyle: 'medium', timeStyle: 'short' });

const LangBadge = ({ lang }) => (
    <span style={{
        fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
        backgroundColor: '#EEF0F8', color: '#0B1157', textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>
        {lang || BASE_LANGUAGE}
    </span>
);

const StatusBadge = ({ valid }) => (
    <span style={{
        fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 999,
        backgroundColor: valid ? '#E6F4EA' : '#FFF3E0',
        color: valid ? '#198754' : '#C97D2C',
    }}>
        {valid ? 'Aktuálna' : 'Zastaraná'}
    </span>
);

// Spoločný UI pre "vygenerovaný dokument" záložky (technická dokumentácia, EÚ vyhlásenie).
// Pre každý jazyk sa sleduje stav nezávisle - SK aj DE môžu byť aktuálne naraz.
// Platná = najnovšia verzia daného jazyka, vygenerovaná po poslednej zmene jednotky.
const GeneratedDocumentTab = ({
    unit, description, note, statusField,
    generateAction, downloadAction, deleteAction,
    generateLabel, generatingLabel, emptyText, languageOptions,
    requireStandardsAssessment, children,
}) => {
    const dispatch = useDispatch();
    const [downloadingId, setDownloadingId] = useState(null);
    const [generationToDelete, setGenerationToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);

    const allGenerations = unit[statusField]?.generations ?? [];

    // Pre každý jazyk vyberieme najnovšiu (index 0 po zoradení) — ostatné idú do histórie
    const latestByLang = {};
    const historyGenerations = [];
    allGenerations.forEach(g => {
        const lang = g.lang || BASE_LANGUAGE;
        if (!latestByLang[lang]) {
            latestByLang[lang] = g;
        } else {
            historyGenerations.push(g);
        }
    });
    const latestRows = Object.values(latestByLang).sort((a, b) =>
        (a.lang || BASE_LANGUAGE).localeCompare(b.lang || BASE_LANGUAGE)
    );

    const handleDownload = (generation) => {
        setDownloadingId(generation._id);
        dispatch(downloadAction({
            packagingUnitId: unit._id,
            generationId: generation._id,
            name: unit.name,
            generatedAt: generation.createdAt,
        }))
            .unwrap()
            .catch(() => {})
            .finally(() => setDownloadingId(null));
    };

    const handleDelete = () => {
        if (!generationToDelete) return;
        setDeleting(true);
        dispatch(deleteAction({ packagingUnitId: unit._id, generationId: generationToDelete._id }))
            .unwrap()
            .then(() => setGenerationToDelete(null))
            .catch(() => {})
            .finally(() => setDeleting(false));
    };

    const tableHeadStyle = { padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left', whiteSpace: 'nowrap' };
    const tableCellStyle = { padding: '10px 12px', verticalAlign: 'middle', fontSize: 13, color: '#0B1157' };

    const renderRows = (rows) => rows.map((generation, i) => (
        <tr
            key={generation._id}
            onMouseEnter={() => setHoveredRow(generation._id)}
            onMouseLeave={() => setHoveredRow(null)}
            style={{
                background: hoveredRow === generation._id ? '#EEF1FB' : i % 2 === 0 ? '#fff' : '#F9FAFF',
                borderTop: '1px solid #EEF0F8',
            }}
        >
            <td style={tableCellStyle}><LangBadge lang={generation.lang} /></td>
            <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>{formatDate(generation.createdAt)}</td>
            <td style={tableCellStyle}>{generation.generatedByName || '—'}</td>
            <td style={tableCellStyle}><StatusBadge valid={generation.valid} /></td>
            <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                <div className="d-flex gap-1 justify-content-end">
                    <Button
                        className="btnIcon"
                        disabled={downloadingId === generation._id}
                        onClick={() => handleDownload(generation)}
                    >
                        <IoDownloadOutline className="icon" size={18} style={{ color: '#718094' }} />
                    </Button>
                    {!generation.valid && (
                        <Button className="btnIcon" onClick={() => setGenerationToDelete(generation)}>
                            <RemoveIcon className="icon icon-remove" alt="Vymazať" />
                        </Button>
                    )}
                </div>
            </td>
        </tr>
    ));

    return (
        <div>
            {description && (
                <p className="text-muted small mb-3" style={{ whiteSpace: 'nowrap' }}>{description}</p>
            )}
            {note && (
                <div
                    className="d-inline-flex align-items-center gap-2 mb-3"
                    style={{ fontSize: 12, padding: '6px 12px', borderRadius: 8, backgroundColor: '#EEF0F8', color: '#5B6178' }}
                >
                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#9AA0B4', flexShrink: 0 }} />
                    {note}
                </div>
            )}

            <GenerateDocumentButton
                unit={unit}
                label={generateLabel}
                generatingLabel={generatingLabel}
                generateAction={generateAction}
                languageOptions={languageOptions}
                requireStandardsAssessment={requireStandardsAssessment}
            />

            {children}

            <div className="fw-semibold mb-2 mt-4" style={{ color: '#0B1157', fontSize: 14 }}>
                Vygenerované verzie
            </div>

            <div style={{ borderRadius: 14, overflowX: 'auto', border: '1px solid #EEF0F8' }}>
                <table style={{ width: '100%', minWidth: 480, borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#F4F5FE' }}>
                            <th style={tableHeadStyle}>Jazyk</th>
                            <th style={tableHeadStyle}>Vygenerované</th>
                            <th style={tableHeadStyle}>Vygeneroval</th>
                            <th style={tableHeadStyle}>Stav</th>
                            <th style={{ ...tableHeadStyle, width: 80 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {latestRows.length > 0 ? renderRows(latestRows) : (
                            <tr>
                                <td colSpan={5} style={{ padding: '24px 12px', textAlign: 'center', color: '#9AA0B4', fontSize: 13 }}>
                                    {emptyText}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {historyGenerations.length > 0 && (
                <div className="mt-3">
                    <button
                        type="button"
                        onClick={() => setHistoryOpen(o => !o)}
                        className="d-flex align-items-center gap-2"
                        style={{ border: 'none', background: 'none', padding: 0, fontSize: 12.5, fontWeight: 600, color: '#5B6178', cursor: 'pointer' }}
                    >
                        <IoChevronDownOutline
                            size={14}
                            style={{ transform: historyOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
                        />
                        Staršie verzie ({historyGenerations.length})
                    </button>

                    {historyOpen && (
                        <div style={{ borderRadius: 14, overflowX: 'auto', border: '1px solid #EEF0F8', marginTop: 8 }}>
                            <table style={{ width: '100%', minWidth: 480, borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#F4F5FE' }}>
                                        <th style={tableHeadStyle}>Jazyk</th>
                                        <th style={tableHeadStyle}>Vygenerované</th>
                                        <th style={tableHeadStyle}>Vygeneroval</th>
                                        <th style={{ ...tableHeadStyle, width: 80 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyGenerations.map((generation, i) => (
                                        <tr
                                            key={generation._id}
                                            onMouseEnter={() => setHoveredRow(generation._id)}
                                            onMouseLeave={() => setHoveredRow(null)}
                                            style={{
                                                background: hoveredRow === generation._id ? '#EEF1FB' : i % 2 === 0 ? '#fff' : '#F9FAFF',
                                                borderTop: '1px solid #EEF0F8',
                                            }}
                                        >
                                            <td style={tableCellStyle}><LangBadge lang={generation.lang} /></td>
                                            <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>{formatDate(generation.createdAt)}</td>
                                            <td style={tableCellStyle}>{generation.generatedByName || '—'}</td>
                                            <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                                                <div className="d-flex gap-1 justify-content-end">
                                                    <Button
                                                        className="btnIcon"
                                                        disabled={downloadingId === generation._id}
                                                        onClick={() => handleDownload(generation)}
                                                    >
                                                        <IoDownloadOutline className="icon" size={18} style={{ color: '#718094' }} />
                                                    </Button>
                                                    <Button className="btnIcon" onClick={() => setGenerationToDelete(generation)}>
                                                        <RemoveIcon className="icon icon-remove" alt="Vymazať" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <Modal show={!!generationToDelete} onHide={() => setGenerationToDelete(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Vymazať dokument</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Naozaj chcete vymazať túto verziu z {generationToDelete && formatDate(generationToDelete.createdAt)}?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setGenerationToDelete(null)}>Zrušiť</Button>
                    <Button variant="danger" disabled={deleting} onClick={handleDelete}>Vymazať</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default GeneratedDocumentTab;
